import { decimal, discountPercentage, money, taxRate } from '@/js/shared/billables/decimal';
import { documentShippingAmount } from '@/js/shared/billables/document_shipping';

export function calculateInvoiceTotals(input) {
  const totals = calculateClientDocumentTotals(input, preserveInputOrder);

  return {
    ...totals,
    deposit: calculateDepositAmount(totals.total, input.deposit),
    balance: money(totals.total)
      .minus(money(input.payments || 0))
      .toFixed(2),
  };
}

export function calculateQuoteTotals(input) {
  return calculateClientDocumentTotals(input, sortByPersistedId);
}

function calculateClientDocumentTotals({ items, discount, tax, documentShipping }, orderItems) {
  const subtotal = money(items.reduce((sum, item) => sum.plus(money(item.total)), decimal(0)));
  const shipping = calculateShippingAmount(items, subtotal, documentShipping);
  const discountAmount = calculateDiscountAmount(subtotal, discount);
  const discountedItems = applyDiscounts(orderItems(items), subtotal, discountAmount);
  const taxableSubtotal = calculateTaxableSubtotal(discountedItems, shipping, tax.taxShipping);
  const taxAmount = taxableSubtotal.times(taxRate(tax.rate)).div('100').round(2);
  const total = subtotal.plus(taxAmount).plus(shipping).minus(discountAmount).round(2);

  return {
    subtotal: subtotal.toFixed(2),
    discount: discountAmount.toFixed(2),
    tax: taxAmount.toFixed(2),
    shipping: shipping.toFixed(2),
    total: total.toFixed(2),
  };
}

function calculateDepositAmount(total, deposit) {
  if (!deposit || !deposit.enabled) {
    return '0.00';
  }

  if (deposit.unit === 'currency') {
    return money(deposit.value).toFixed(2);
  }

  return money(total).times(decimal(deposit.value)).div('100').round(2).toFixed(2);
}

function calculateShippingAmount(items, subtotal, documentShipping) {
  return (
    documentShippingAmount(subtotal, documentShipping) ??
    money(items.reduce((sum, item) => sum.plus(money(item.shipping)), decimal(0)))
  );
}

function calculateDiscountAmount(subtotal, discount) {
  if (discount.unit === 'currency') {
    return money(discount.value);
  }

  return subtotal.times(discountPercentage(discount.value)).div('100').round(2);
}

function applyDiscounts(items, subtotal, discountAmount) {
  let discountLeft = discountAmount;

  return items.map((item) => {
    const itemTotal = money(item.total);
    let itemDiscount = subtotal.gt('0')
      ? discountAmount.div(subtotal).times(itemTotal).round(2)
      : decimal(0);

    if (discountLeft.gt('0') && discountLeft.lt(itemDiscount)) {
      itemDiscount = discountLeft;
    } else if (discountLeft.lte('0')) {
      itemDiscount = decimal(0);
    }

    discountLeft = discountLeft.minus(itemDiscount);

    return {
      ...item,
      totalAfterDiscount: itemTotal.minus(itemDiscount),
    };
  });
}

function calculateTaxableSubtotal(items, shipping, taxShipping) {
  const taxableItemsTotal = items
    .filter((item) => item.taxable)
    .reduce((sum, item) => sum.plus(item.totalAfterDiscount), decimal(0));

  return taxableItemsTotal.plus(taxShipping ? shipping : decimal(0)).round(2);
}

function preserveInputOrder(items) {
  return items;
}

function sortByPersistedId(items) {
  return [...items].sort(compareByPersistedId);
}

function compareByPersistedId(first, second) {
  return (first.id ?? Number.MAX_SAFE_INTEGER) - (second.id ?? Number.MAX_SAFE_INTEGER);
}
